import React, { useState, useEffect, useCallback } from 'react'
import { Button, Checkbox } from '../../../components'
import { useMessage, useModalDialog } from '../../../hooks'
import { useBuddyDialog } from '../hooks'
import { generationService } from '../services'
import '../index.css'

const PAGE_SIZE = 20

const History = () => {
  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const { showDeleteDialog, showDeleteAllDialog, showEditDatesDialog } = useBuddyDialog()

  const [tab, setTab] = useState('active') // 'active' | 'deleted'
  const [generations, setGenerations] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [selectedIds, setSelectedIds] = useState([])
  const [loading, setLoading] = useState(true)

  const load = useCallback((signal) => {
    setLoading(true)
    return generationService.getHistory({ deleted: tab === 'deleted', start: start || undefined, end: end || undefined, page, pageSize: PAGE_SIZE }, signal)
      .then(res => { setGenerations(res.generations); setTotal(res.total); setLoading(false) })
      .catch(err => { if (err.name !== 'AbortError') { showMessage('Kunde inte ladda historik', 'error'); setLoading(false) } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, start, end, page])

  useEffect(() => {
    const ctrl = new AbortController()
    setSelectedIds([])
    load(ctrl.signal)
    return () => ctrl.abort()
  }, [load])

  const changeTab = (newTab) => { setTab(newTab); setPage(1) }

  const toggleSelected = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const handleDelete = () => {
    showDeleteDialog(selectedIds.length, () => {
      generationService.softDelete({ ids: selectedIds })
        .then(() => { hideModalDialog(); showMessage('Borttaget'); load() })
        .catch(err => { hideModalDialog(); showMessage(err.message || 'Kunde inte ta bort', 'error') })
    })
  }

  const handleDeleteAll = () => {
    showDeleteAllDialog(() => {
      generationService.softDelete({ all: true })
        .then(() => { hideModalDialog(); showMessage('All historik borttagen'); load() })
        .catch(err => { hideModalDialog(); showMessage(err.message || 'Kunde inte ta bort', 'error') })
    })
  }

  const handleRestore = () => {
    generationService.restore(selectedIds)
      .then(() => { showMessage('Återställt'); load() })
      .catch(err => showMessage(err.message || 'Kunde inte återställa', 'error'))
  }

  const handleUpdateDates = (generation) => (dates) => {
    generationService.updateDates(generation.generation_id, dates)
      .then(() => { hideModalDialog(); showMessage('Datum uppdaterat.'); load() })
      .catch(err => { hideModalDialog(); showMessage(err.message || 'Kunde inte uppdatera datum', 'error') })
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="bud-page">
      <div className="bud-page-header">
        <h1>Historik</h1>
      </div>

      <div className="bud-history-toolbar">
        <div className="bud-tab-switch">
          <button type="button" className={"bud-tab-btn" + (tab === 'active' ? ' active' : '')} onClick={() => changeTab('active')}>Aktiva</button>
          <button type="button" className={"bud-tab-btn" + (tab === 'deleted' ? ' active' : '')} onClick={() => changeTab('deleted')}>Papperskorg</button>
        </div>

        <div className="bud-date-filter">
          <input className="bud-plain-input" type="date" value={start} onChange={e => { setStart(e.target.value); setPage(1) }} />
          <span>–</span>
          <input className="bud-plain-input" type="date" value={end} onChange={e => { setEnd(e.target.value); setPage(1) }} />
        </div>

        <div className="bud-history-actions">
          {selectedIds.length > 0 && tab === 'active' &&
            <Button value="Ta bort valda" className="button bud-danger-button" onClick={handleDelete} />
          }
          {selectedIds.length > 0 && tab === 'deleted' &&
            <Button value="Återställ valda" className="button" onClick={handleRestore} />
          }
          {tab === 'active' && generations.length > 0 &&
            <Button value="Ta bort allt" className="button bud-danger-button" onClick={handleDeleteAll} />
          }
        </div>
      </div>

      {loading
        ? <p style={{ color: 'var(--color-dark-grey)' }}>Laddar…</p>
        : generations.length === 0
          ? <p style={{ color: 'var(--color-dark-grey)' }}>{tab === 'active' ? 'Inga rundor än.' : 'Papperskorgen är tom.'}</p>
          : (
            <div className="bud-history-list">
              {generations.map(g => (
                <div className="bud-history-row" key={g.generation_id}>
                  <Checkbox checked={selectedIds.includes(g.generation_id)} onChange={() => toggleSelected(g.generation_id)} />
                  <div className="bud-history-row-content">
                    <div className="bud-history-row-date">
                      {new Date(g.generation_timestamp).toLocaleString('sv-SE')}
                      {g.round_end_timestamp && <> — slutade {new Date(g.round_end_timestamp).toLocaleDateString('sv-SE')}</>}
                      <button
                        type="button"
                        className="icon-edit bud-edit-dates-btn"
                        onClick={() => showEditDatesDialog(g, handleUpdateDates(g))}
                        title="Ändra datum"
                      />
                    </div>
                    <div className="bud-pair-list">
                      {g.pairs.map((pair, i) => (
                        <div className="bud-pair-row" key={i}>
                          <span>{pair.display_name_a}</span>
                          <span className="bud-pair-connector">↔</span>
                          <span>{pair.display_name_b}</span>
                          {g.mega_buddy_user_id && (pair.user_id_a === g.mega_buddy_user_id || pair.user_id_b === g.mega_buddy_user_id) &&
                            <span className="pill bud-pill-mega">Mega-buddy</span>
                          }
                        </div>
                      ))}
                    </div>
                    {g.excluded.length > 0 &&
                      <div className="bud-excluded-list">
                        <span className="bud-excluded-label">Undantagna:</span> {g.excluded.map(e => e.display_name).join(', ')}
                      </div>
                    }
                  </div>
                </div>
              ))}
            </div>
          )
      }

      {pageCount > 1 &&
        <div className="bud-pagination">
          <Button value="‹" className="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} />
          <span className="bud-pagination-label">Sida {page} av {pageCount}</span>
          <Button value="›" className="button" onClick={() => setPage(p => Math.min(pageCount, p + 1))} disabled={page >= pageCount} />
        </div>
      }
    </div>
  )
}

export default History
